import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { BaseButton, BaseInput } from "@wantable/Shared/Components";
import { useAuth } from "@wantable/Shared/Hooks";
import { ErrorIcon, SuccessIcon } from "@wantable/Shared/Icons";

import CloseButton from "../Shared/CloseButton";
import { validationSchema } from "./validator";
import { addEnterButtonListener } from "../../helpers/addEnterButtonListener";
import { ModalType}  from "../types";

interface IResetPasswordFormProps {
  onClose: () => void;
  onAfterLogin?: () => void;
}

type ResetPasswordFormStatus = "error" | "success" | null;

const ResetPasswordForm = ({ onClose, onAfterLogin }: IResetPasswordFormProps) => {
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordFormStatus, setResetPasswordFormStatus] =
    useState<ResetPasswordFormStatus>(null);
  const [formMessage, setFormMessage] = useState("");
  const { resetPassword } = useAuth();
  const { values, setFieldValue, handleSubmit, errors, touched } = useFormik({
    initialValues: {
      password: "",
      passwordConfirmation: ""
    },
    validateOnBlur: true,
    validationSchema,
    onSubmit: async ({ password, passwordConfirmation }) => {
      try {
        setResettingPassword(true);

        const token = (new URLSearchParams(window.location.search)).get('reset_password_token') || '';
        await resetPassword(token, password, passwordConfirmation);

        if (onAfterLogin) {
          onAfterLogin();
        }

        onClose();
        setResetPasswordFormStatus("success");
      } catch (error) {
        // @ts-ignore
        setFormMessage(error.message);
        setResetPasswordFormStatus("error");
      } finally {
        setResettingPassword(false);
      }
    }
  });

  useEffect(() => {
    return addEnterButtonListener(handleSubmit);
  }, []);

  return (
    <div>
      {resetPasswordFormStatus === "success" && (
        <div
          className="w-full mx-[13px] md:w-[440px] bg-white rounded-10 py-[30px] px-[15px] md:px-[56px] flex flex-col items-center cursor-auto relative">
          <CloseButton onClick={onClose} />
          <h2 className="text-mobile-heading-3 md:text-desktop-heading-3 font-black font-primary text-center">
            Password reset email sent
          </h2>
          <p className="mt-[12px] mb-[10px] text-mobile-body-lg font-secondary text-center">
            We've sent a link to reset your password to your email. Follow the directions in the email to reset your password.
            <br />
            If you can't find the email check your spam folder.
          </p>
        </div>
      )}
      {resetPasswordFormStatus !== "success" && (
        <form
          onClick={(event) => event.stopPropagation()}
          onSubmit={(event) => event.preventDefault()}
          className="w-full mx-[13px] md:w-[440px] bg-white rounded-10 py-[30px] px-[15px] md:px-[56px] flex flex-col items-center cursor-auto relative"
        >
          <CloseButton onClick={onClose} />

          <h2 className="text-mobile-heading-3 md:text-desktop-heading-3 font-black font-primary text-center">
            Reset your password
          </h2>
          <p className="mt-[12px] mb-[10px] text-mobile-body-lg font-secondary text-center">
            Enter a new password below
          </p>
          <div
            className={`w-full flex flex-row items-center justify-center text-mobile-body md:text-desktop-body text-core-red font-medium font-secondary mb-[15px]`}
          >
            <ErrorIcon hidden={resetPasswordFormStatus !== "error"} className="pr-4" />
            {formMessage}
          </div>

          <fieldset className="w-full flex flex-col items-center gap-[10px]">
            <BaseInput
              type="password"
              placeholder="Enter new password"
              name="New Password"
              label="Password"
              value={values.password}
              onChange={(value) => setFieldValue("password", value)}
              onBlur={() => {}}
              error={touched.password ? errors.password : undefined}
            />
            <BaseInput
              type="password"
              placeholder="Re-enter new password"
              name="passwordConfirmation"
              label="Confirm New Password"
              value={values.passwordConfirmation}
              onChange={(value) => setFieldValue("passwordConfirmation", value)}
              onBlur={() => {}}
              error={touched.passwordConfirmation ? errors.passwordConfirmation : undefined}
            />

            <div className="w-full">
              <BaseButton
                mode="button"
                onClick={handleSubmit}
                fullWidth
                isSubmitButton
                disabled={resettingPassword}
                showSpinner={resettingPassword}
              >
                Reset Password
              </BaseButton>
            </div>
          </fieldset>
        </form>
      )}
    </div>
  );
};

export default ResetPasswordForm;
